import { auth } from '../firebase'
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
  deleteUser as firebaseDeleteUser,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth'
import { httpService } from '../http.service'
import toast from 'react-hot-toast'

export const userService = {
  getUsers,
  getById,
  getByUsername,
  signup,
  login,
  loginWithGoogle,
  logout,
  validateBackendToken,
  resetPassword,
  sendVerificationEmail,
  updateUserProfile,
  deleteUserAccount,
  isEmailVerified,
  getTopEditors,
}

// 1) getUsers
async function getUsers(filterBy = {}) {
  try {
    let query = ''
    if (filterBy.userIds?.length) {
      query = '?userIds=' + filterBy.userIds.join(',')
    }
    return await httpService.get(`user${query}`)
  } catch (err) {
    console.error('Failed to get users', err)
    throw err
  }
}

// 2) getById
async function getById(userId) {
  if (!userId) throw new Error('User ID is required')
  try {
    return await httpService.get(`user/${userId}`)
  } catch (err) {
    console.error(`Failed to get user by id ${userId}`, err)
    throw err
  }
}

async function getByUsername(username) {
  if (!username) throw new Error('Username is required')
  try {
    return await httpService.get(`user/by-username/${username}`)
  } catch (err) {
    console.error(`❌ Failed to get user by username "${username}"`, err)
    throw err
  }
}

// 3) signup
async function signup({ email, password, nickname, username }) {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    )
    const user = userCredential.user

    await updateProfile(user, { displayName: nickname })
    const idToken = await user.getIdToken(true)

    const { user: userData } = await httpService.post('auth/signup', {
      token: idToken,
      nickname,
      username,
    })

    return userData
  } catch (err) {
    console.error('Failed to signup', err)
    throw err
  }
}

// 4) login
async function login({ email, password }) {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    )
    const user = userCredential.user

    await user.reload()
    const idToken = await user.getIdToken(true)

    const { user: userData } = await httpService.post('auth/login', {
      token: idToken,
    })

    return userData
  } catch (err) {
    // מחזיר אובייקט עם code ו־message בלבד
    throw {
      code: err.code || '',
      message: err.message || 'שגיאה לא ידועה',
    }
  }
}

// 5) login with Google
async function loginWithGoogle() {
  try {
    const provider = new GoogleAuthProvider()
    const result = await signInWithPopup(auth, provider)
    const user = result.user

    await user.reload()
    const idToken = await user.getIdToken(true)

    const { user: userData } = await httpService.post(
      'auth/login',
      { token: idToken },
      { withCredentials: true }
    )

    return userData
  } catch (err) {
    console.error('Failed to login with Google', err)
    throw err
  }
}

// 6) validateBackendToken
async function validateBackendToken() {
  try {
    const res = await httpService.post(
      'auth/validate-token',
      {},
      { withCredentials: true }
    )
    return res.user || null
  } catch (err) {
    return null
  }
}

// 7) logout
async function logout() {
  try {
    await signOut(auth)
    await httpService.post('auth/logout', {}, { withCredentials: true })
  } catch (err) {
    console.error('Failed to logout', err)
    throw err
  }
}

// 8) resetPassword
async function resetPassword(email) {
  try {
    const result = await httpService.post('auth/reset-password', { email })
    if (!result.success) throw new Error(result.message)
    return result
  } catch (error) {
    console.error('❌ שגיאה באיפוס סיסמה:', error)
    throw new Error(error.message)
  }
}

// 9) sendVerificationEmail
async function sendVerificationEmail() {
  try {
    const user = auth.currentUser
    if (!user || !user.email) throw new Error('No user/email in session.')

    const result = await httpService.post('auth/send-verification-email', {
      email: user.email,
    })
    if (result.error) throw new Error(result.error)
    return result
  } catch (err) {
    console.error('Failed to send verification email', err)
    throw err
  }
}

// 10) updateUserProfile
async function updateUserProfile(updates) {
  try {
    const user = auth.currentUser
    if (!user) throw new Error('No user is signed in.')

    await updateProfile(user, updates)

    const userFromServer = await httpService.get(`user/${user.uid}`)
    const updatedUserData = { ...userFromServer, ...updates }

    await httpService.put(`user/${user.uid}`, updatedUserData)
    return updatedUserData
  } catch (err) {
    console.error('Failed to update user profile', err)
    throw err
  }
}

// 11) deleteUserAccount
async function deleteUserAccount(password) {
  try {
    const user = auth.currentUser
    if (!user) throw new Error('No user is signed in.')

    const credential = EmailAuthProvider.credential(user.email, password)
    await reauthenticateWithCredential(user, credential)
    await firebaseDeleteUser(user)

    await httpService.delete(`user/${user.uid}`)
  } catch (err) {
    console.error('Failed to delete user account', err)
    throw err
  }
}

// 12) isEmailVerified
async function isEmailVerified() {
  try {
    const user = auth.currentUser
    if (!user) throw new Error('No user is signed in.')
    await user.reload()
    return user.emailVerified
  } catch (err) {
    console.error('Failed to check if email is verified', err)
    throw err
  }
}

// 13) getTopEditors
async function getTopEditors() {
  try {
    return await httpService.get('user/top-editors')
  } catch (err) {
    console.error('Failed to fetch top editors', err)
    throw err
  }
}
